
const objectId = '322db622-3cac-4ac7-ab84-c5a7d9724924';

const getAccessToken = async (instance, activeAccount) => {
  const request = {
    scopes: ["user.read"],
    account: activeAccount,
  };

  try {
    const response = await instance.acquireTokenSilent(request);
    return response.accessToken;
  } catch (error) {
    try {
      await instance.initialize();
      const response = await instance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      const response = await instance.acquireTokenRedirect(request);
      return response.accessToken;
    }
  }
};

export async function getUserDetails(token, email) {  
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  if (!email || typeof email !== 'string') {
    console.error('Invalid email: Email is required and must be a string');
    throw new Error('Invalid email format: Email is required');
  }
  
  const trimmedEmail = email.trim();
  
  if (!trimmedEmail) {
    throw new Error('Invalid email format: Email cannot be empty');
  }
  
  if (!isValidEmail(trimmedEmail)) {
    throw new Error('Invalid email format: Please provide a valid email address');
  }
  
  try {
    const resp = await fetch(
      `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(trimmedEmail)}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (resp.status === 404) {
      console.warn(`User not found in Azure AD: ${trimmedEmail}`);
      throw new Error(`User with email ${trimmedEmail} not found in Azure Active Directory`);
    }
    
    if (resp.status === 403) {
      console.error('Insufficient permissions to access user details');
      throw new Error('Insufficient permissions to access user details. Please check token permissions.');
    }
    
    if (!resp.ok) {
      const errorText = await resp.text();
      console.error("Failed to get user details:", resp.status, errorText);
      throw new Error(`Failed to fetch user details: ${resp.status} ${resp.statusText}`);
    }

    const data = await resp.json();
    
    if (!data?.id) {
      console.error('User data received but no ID found:', data);
      throw new Error('User found but no ID available');
    }
    
    return data.id;
    
  } catch (error) {
    // Re-throw custom errors as-is
    if (error.message.includes('Invalid email format') || 
        error.message.includes('not found in Azure Active Directory') ||
        error.message.includes('Insufficient permissions')) {
      throw error;
    }
    
    // Handle network/other errors
    console.error('Network or unexpected error:', error.message);
    throw new Error(`Failed to fetch user details: ${error.message}`);
  }
}

export async function addUserInAdGroup(email, instance, activeAccount) {  
  try {
    const token = await getAccessToken(instance, activeAccount);
    const id = await getUserDetails(token, email);
        
    const resp = await fetch(
      `https://graph.microsoft.com/v1.0/groups/${objectId}/members/$ref`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          "@odata.id": `https://graph.microsoft.com/v1.0/directoryObjects/${id}`,
        }),
      }
    );

    const text = await resp.text();
    
    if (resp.status === 400) {
      throw new Error('User is already a member of this group or invalid request');
    }
    
    if (!resp.ok) {
      throw new Error(`Failed to add user to group: ${resp.status} ${text}`);
    }
    
    return resp.status;
  } catch (error) {
    console.error("Error adding user to AD group:", error.message);
    throw error;
  }
}

export async function removeMultipleUsersFromAdGroup(emails, instance, activeAccount) {
  if (!Array.isArray(emails) || emails.length === 0) {
    throw new Error('Invalid email array provided');
  }
  
  const results = {
    successfulRemovals: [],
    failedRemovals: [],
    notFoundUsers: []
  };
  
  try {
    const token = await getAccessToken(instance, activeAccount);
    
    // Process each user sequentially to avoid rate limiting
    for (const email of emails) {
      try {        
        // Get user ID from email
        const userId = await getUserDetails(token, email);
        
        // Remove from group
        const resp = await fetch(
          `https://graph.microsoft.com/v1.0/groups/${objectId}/members/${userId}/$ref`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        if (resp.status === 200 || resp.status === 204) {
          results.successfulRemovals.push(email);
        } else if (resp.status === 404) {
          results.notFoundUsers.push(email);
        } else {
          results.failedRemovals.push(email);
          console.error(`Failed to remove ${email}: ${resp.status}`);
        }
        
        // Add small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 100));
        
      } catch (userError) {
        console.error(`Error processing ${email}:`, userError.message);
        
        if (userError.message.includes('not found in Azure Active Directory')) {
          results.notFoundUsers.push(email);
        } else {
          results.failedRemovals.push(email);
        }
      }
    }
        
    // Return results for handling in the calling function
    return results;
    
  } catch (error) {
    console.error("Error in bulk AD group removal:", error.message);
    throw error;
  }
}

export async function removeUserFromAdGroup(email, instance, activeAccount) {

  try {
    const token = await getAccessToken(instance, activeAccount);
    const userId = await getUserDetails(token, email);

    const resp = await fetch(
      `https://graph.microsoft.com/v1.0/groups/${objectId}/members/${userId}/$ref`,
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (resp.status === 200 || resp.status === 204) {
      return { success: true };
    } else if (resp.status === 404) {
      return { success: false, reason: 'not_found' };
    } else {
      return { success: false, reason: 'failed' };
    }
  } catch (error) {
    console.error("Error removing user from AD group:", error.message);
    throw error;
  }
}